import styled, { css } from 'styled-components'
import {
	backgroundOrange,
	darkOrange,
	orange,
	gold,
	danger,
	dangerTint,
	black,
	textMuted,
	mutedGrey,
	surface2,
	warmTint,
} from '@/theme/colors'
import { radii, motion } from '@/theme/tokens'
import { fontSizes, text, leading } from '@/theme/typography'
import { Button } from '@components/Button'
import { cardSurface } from '@components/Card'
import { Pill } from '@components/Pill'
import { ChatDiscussion } from '@components/Chat'


export const LoadingContainer = styled.div`
    display: flex;
    flex: 1;
    justify-content: center;
    align-items: center;
    background-color: ${backgroundOrange};
    height: 100%;
`


export const NotFoundMessage = styled.div`
    padding: 32px;
    text-align: center;
    color: ${textMuted};
`


export const DetailCard = styled.div`
	${cardSurface}
	padding: 0;
	overflow: hidden;
`

export const DetailHeader = styled.div`
    padding: 24px 24px 0;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
`

/* The back arrow and the title share a line; the arrow holds the top when the title wraps. */
export const TitleRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    min-width: 0;
`

export const DetailTitle = styled.h1`
    margin: 0;
    font-size: ${fontSizes.headingLg};
    font-weight: 700;
    color: ${black};
    line-height: ${leading.snug};
    word-break: break-word;
`

export const ActionButtons = styled.div`
    display: flex;
    gap: 8px;
    flex-shrink: 0;
`

export const IconButton = styled(Button).attrs({ variant: 'secondary' as const, size: 'sm' as const })<{ $variant?: 'default' | 'danger' }>`
	width: 36px;
	height: 36px;
	padding: 0;

	${({ $variant }) =>
		$variant === 'danger' &&
		css`
			color: ${danger};
			border-color: ${danger}40;

			&:hover:not(:disabled) {
				background: ${dangerTint};
				color: ${danger};
				border-color: ${danger};
			}
		`}
`

export const DetailBody = styled.div`
    padding: 20px 24px 24px;
    display: flex;
    flex-direction: column;
    gap: 24px;
`

/* ---- Location Section ---- */

export const LocationSection = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`

export const LocationIcon = styled.div`
    color: ${orange};
    flex-shrink: 0;
    display: flex;
`

export const LocationText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
    flex: 1;
`

export const LocationPrimary = styled.span`
    font-size: ${fontSizes.base};
    font-weight: 500;
    color: ${black};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`

export const LocationSecondary = styled.span`
    font-size: ${fontSizes.micro};
    color: ${textMuted};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`

export const MapButton = styled.a`
    font-size: ${fontSizes.micro};
    font-weight: 700;
    color: ${orange};
    text-decoration: none;
    padding: 6px 12px;
    border: 1px solid ${orange}40;
    border-radius: ${radii.small};
    flex-shrink: 0;
    transition: all ${motion.fast};
    white-space: nowrap;

    &:hover {
        background: ${gold}1f;
        border-color: ${orange};
    }
`

/* ---- Dates Section ---- */

export const DatesSection = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    padding: 16px;
    background: ${backgroundOrange};
    border-radius: ${radii.inner};
`

export const DateBlock = styled.div<{ $align?: 'left' | 'right' }>`
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: ${({ $align }) => ($align === 'right' ? 'flex-end' : 'flex-start')};
    text-align: ${({ $align }) => ($align === 'right' ? 'right' : 'left')};
`

export const DateBlockLabel = styled.span`
    ${text.eyebrow}
    color: ${gold};
`

export const DateBlockValue = styled.span`
    font-size: ${fontSizes.base};
    font-weight: 700;
    color: ${black};
`

export const DateBlockTime = styled.span`
    font-size: ${fontSizes.caption};
    color: ${textMuted};
`

/* ---- Guests Section ---- */

export const SectionLabel = styled.h3`
    ${text.eyebrow}
    color: ${darkOrange};
    margin: 0 0 12px;
`

export const GuestList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`

export const GuestItem = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 12px;
    background: ${surface2};
    border-radius: ${radii.small};
`


export const GuestName = styled.span`
    font-size: ${fontSizes.small};
    font-weight: 500;
    color: ${black};
    flex: 1;
`

export const YouPill = styled(Pill).attrs({ $tone: 'success' as const })``

/* ---- Links Section ---- */

export const LinksList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`

export const LinkItem = styled.a`
    font-size: ${fontSizes.small};
    color: ${orange};
    text-decoration: none;
    padding: 10px 12px;
    background: ${surface2};
    border-radius: ${radii.small};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: block;
    transition: background ${motion.fast};

    &:hover {
        background: ${gold}1f;
        text-decoration: underline;
    }
`

/* ---- Attachments Section ---- */

export const AttachmentButton = styled.button`
    font-size: ${fontSizes.small};
    color: ${orange};
    font-weight: 500;
    padding: 10px 12px;
    background: ${surface2};
    border: none;
    border-radius: ${radii.small};
    cursor: pointer;
    font-family: inherit;
    text-align: left;
    width: 100%;
    transition: background ${motion.fast};

    &:hover {
        background: ${gold}1f;
    }
`

/* ---- Notes Section ---- */

export const NotesContent = styled.div`
    font-size: ${fontSizes.small};
    color: ${black};
    line-height: ${leading.body};
    white-space: pre-wrap;
    word-break: break-word;
    padding: 12px;
    background: ${surface2};
    border-radius: ${radii.small};
`

/* ---- Metadata ---- */

export const MetaSection = styled.div`
    font-size: ${fontSizes.micro};
    color: ${mutedGrey};
    border-top: 1px solid ${warmTint};
    padding-top: 16px;
    display: flex;
    gap: 24px;
`

/** Said in place of the guests when nobody is staying. */
export const NobodyText = styled.p`
	${text.bodyXs}
	margin: 0;
	color: ${textMuted};
`

/** "I'm staying here", under the guests. */
export const MembershipRow = styled.div`
	display: flex;
	margin-top: 12px;
`

/** The accommodation's Discussion, under its card. */
export const Discussion = styled(ChatDiscussion)`
	margin-top: 24px;
`
